import Vue from 'vue'
import Vuex from 'vuex'

import auth from '../auth/store/store'
import layout from '../layout/store/store'
import messages from '../messages/store/store'
import settings from '../settings/store/store'
import tools from '../tools/store/store'

Vue.use(Vuex)

const AppStore = new Vuex.Store({
  state: {
    homeRoute: 'dashboard',
    route: { name: '', params: {}, query: {} },

    /**
     * @constant {Object} landingPageMap
     * @desc Maps landing pages to store names.
     * The map is used to determine which store to load based on the route.
     * The keys are the route names or their paths.
     * The values are the corresponding store names.
     * Landing pages are special routes that do not correspond to their
     * route's reportName value.
     */
    landingPageMap: {
      account: 'admin', // Alias to settings.
      admin: 'admin', // Alias to settings.
      settings: 'settings',

      'custom-reports': 'custom-reports',
      dashboard: 'dashboard',
      docs: 'docs',
      internal: 'internal',
      releases: 'docs',
    },
    /**
     * @constant {Object} storeNameMap
     * @desc Maps route names to store names.
     * The map is used to determine which store to load based on the route name.
     * The keys are the route names or their paths.
     * The values are the corresponding store names.
     */
    storeNameMap: {
      // Home
      '': 'dashboard',
      dashboard: 'dashboard',

      // Auth
      'sign-in': 'auth',
      'sign-out': 'auth',
      'sign-up': 'auth',
      'forgot-password': 'auth',
      'reset-password': 'auth',

      // Docs
      docs: 'docs',
      releases: 'docs',

      // Settings
      settings: 'settings',

      // Admin
      account: 'admin',
      admin: 'admin',

      enterprise: 'admin',
      logs: 'admin',
      profile: 'admin',
      'project-sync': 'admin',
      security: 'admin',
      users: 'admin',

      // Internal
      internal: 'internal',
      person: 'internal',
      organization: 'internal',
      project: 'internal',
      time: 'internal',

      // Reports
      admanager: 'admanager',
      'adx-health': 'delivery',
      'adx-and-ob': 'delivery',
      'yield-partner': 'delivery', // @deprecated

      discrepancy: 'delivery',
      pacing: 'delivery',
      'network-health': 'delivery',

      // Custom Reports
      'custom-reports': 'custom-reports',
    },
  },
  mutations: {
    route(state, route) {
      state.route = route
    },
  },
  getters: {
    routeName(state) {
      return state.route.name
    },
    reportName(state) {
      return state.route.params?.reportName || state.homeRoute
    },
    reportPath(state) {
      return state.route.params?.reportPath
    },
    storeName(state, getters) {
      // Landing Pages: ['custom-reports', 'docs']
      const storeKey = state.landingPageMap[getters.routeName] || getters.reportName

      return state.storeNameMap[storeKey]
    },
  },
  actions: {
    isStoreRegistered(context, moduleName) {
      return AppStore.hasModule(moduleName)
    },

    getStoreName({ state }, { routeName, reportName }) {
      // Landing Pages: ['custom-reports', 'docs']
      const storeKey = state.landingPageMap[routeName] || reportName

      return state.storeNameMap[storeKey]
    },

    async getStore(context, storeName) {
      const storeMap = {
        admanager: () => import('@/admanager/store/store'),
        dashboard: () => import('@/dashboard/store/store'),
        delivery: () => import('@/delivery/store/store'),
        programmatic: () => import('@/programmatic/store/store'),

        settings: () => import('@/settings/store/store'),

        account: () => import('@/admin/store/store'),
        admin: () => import('@/admin/store/store'),
        profile: () => import('@/admin/store/store'),
        security: () => import('@/admin/store/store'),
        users: () => import('@/admin/store/store'),

        docs: () => import('@/docs/store/store'),
        releases: () => import('@/docs/store/store'),

        discrepancy: () => import('@/delivery/store/store'),
        pacing: () => import('@/delivery/store/store'),

        'network-health': () => import('@/programmatic/store/store'),
        'adx-health': () => import('@/programmatic/store/store'),
        'yield-partner': () => import('@/programmatic/store/store'), // @deprecated
        'adx-and-ob': () => import('@/programmatic/store/store'),

        'custom-reports': () => import('@/custom-reports/store/store'),

        internal: () => import('@/internal/store/store'),
        organization: () => import('@/internal/store/store'),
        person: () => import('@/internal/store/store'),
        project: () => import('@/internal/store/store'),
        time: () => import('@/internal/store/store'),
        'time-page': () => import('@/internal/store/store'),
      }
      const { default: newStore } = await storeMap[storeName]()

      return newStore
    },

    async registerModule({ dispatch }, storeName) {
      const newStore = await dispatch('getStore', storeName)

      AppStore.registerModule(storeName, newStore)

      return dispatch('isStoreRegistered', storeName)
    },
  },
  modules: {
    auth,
    layout,
    messages,
    settings,
    tools,
  },
})

export default AppStore
